// File: frontend/src/components/marketing/MarketingHeader.js
//
// Responsive sticky public header. Desktop: brand + section anchors + theme
// toggle + Sign In / Start Free (or Open Dashboard when already authenticated).
// Mobile: brand + theme toggle + menu button opening an accessible MUI Drawer.
//
// Auth CTAs use React Router navigation (no full reload). Sign In → login mode,
// Start Free → the existing registration mode of the SAME /login page. No
// separate register/forgot routes, and NO password-recovery link anywhere here.
//
// Optional props let a public page other than "/" (the /ambassador page) swap
// in its own wordmark suffix, actions and nav breakpoint. Every default is the
// main site's existing behaviour, unchanged.

import React, { useEffect, useState } from "react";
import {
  Box, Button, Container, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemText, Stack, Divider, useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useLocation } from "react-router-dom";
import PesaFiiBrand from "../PesaFiiBrand";
import ThemeToggle from "./ThemeToggle";

// Site-wide links. The section anchors are written as "/#…" so they work from
// every public page: on "/" the browser treats them as in-page jumps, and from
// /contact or /privacy they open the landing page at that section. A bare
// "#features" on a page without that section is a link that does nothing.
// Contact is a route (`to`), rendered as a client-side link.
export const NAV_LINKS = [
  { label: "Product", href: "/#product" },
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", to: "/contact" },
];

const linkKey = (l) => l.to || l.href;

// A route (`to`) is a client-side link; a plain `href` is a native anchor, so
// the browser handles smooth scroll (see CSS) and the hash updates for deep
// links. Both render as a real <a>.
const linkProps = (l) => (l.to ? { component: RouterLink, to: l.to } : { component: "a", href: l.href });

// Which landing-page section is in view, for the nav's "you are here" mark.
// Only on "/", only for the section links the nav actually has.
function useSectionInView(links) {
  const { pathname } = useLocation();
  const [current, setCurrent] = useState(null);
  const ids = links.map((l) => (l.href || "").match(/^\/#([\w-]+)$/)?.[1]).filter(Boolean).join(",");
  useEffect(() => {
    if (pathname !== "/" || !ids || typeof IntersectionObserver === "undefined") return undefined;
    const seen = new Map();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
      setCurrent(ids.split(",").find((id) => seen.get(id)) || null);
    }, { rootMargin: "-45% 0px -50% 0px" });
    ids.split(",").forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [pathname, ids]);
  return current;
}

function NavLink({ link, onClick, sx, inView }) {
  const { pathname } = useLocation();
  return (
    <Box
      {...linkProps(link)}
      aria-current={link.to && link.to === pathname ? "page" : undefined}
      data-in-view={inView || undefined}
      onClick={onClick}
      sx={{
        color: "text.secondary",
        textDecoration: "none",
        fontSize: 14.5,
        fontWeight: 600,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        whiteSpace: "nowrap",
        position: "relative",
        transition: "color 0.2s",
        "&:hover, &[aria-current='page'], &[data-in-view]": { color: "text.primary" },
        // A short underline for the section in view on "/".
        "&::after": {
          content: '""', position: "absolute", left: 8, right: 8, bottom: -2, height: 2, borderRadius: 99,
          bgcolor: "primary.main", transform: "scaleX(0)", transition: "transform 0.25s ease",
        },
        "&[data-in-view]::after": { transform: "scaleX(1)" },
        "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
        ...sx,
      }}
    >
      {link.label}
    </Box>
  );
}

/**
 * @param {Array} navLinks  Links for the page being shown: `{ label, href }` for
 *   an anchor or `{ label, to }` for a route. Defaults to the site-wide
 *   NAV_LINKS. A page with its own sections (/ambassador) passes its in-page
 *   anchors instead.
 * @param {string} brandSuffix     Optional product name shown after the logo
 *   ("Ambassadors"), sm and up.
 * @param {Function} renderActions Optional ({ stacked, close }) => node that
 *   replaces the default Sign In / Start Free / Open Dashboard buttons, in both
 *   the desktop bar and the drawer.
 * @param {node} mobileAction      Optional primary action shown in the mobile
 *   bar from sm up, where there is room beside the menu button.
 * @param {string} navBreakpoint   Breakpoint at which the inline nav replaces
 *   the drawer. Defaults to "md".
 */
export default function MarketingHeader({
  isAuthed,
  navLinks = NAV_LINKS,
  brandSuffix,
  renderActions,
  mobileAction,
  navBreakpoint = "md",
}) {
  const [open, setOpen] = useState(false);
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const inView = useSectionInView(navLinks);
  const close = () => setOpen(false);

  const authButtons = (stacked = false) => (
    isAuthed ? (
      <Button
        component={RouterLink}
        to="/dashboard"
        variant="contained"
        fullWidth={stacked}
        onClick={close}
        sx={{ fontWeight: 700, borderRadius: 2, textTransform: "none" }}
      >
        Open Dashboard
      </Button>
    ) : (
      <>
        <Button
          component={RouterLink}
          to="/login?mode=login"
          variant="text"
          fullWidth={stacked}
          onClick={close}
          sx={{ fontWeight: 600, borderRadius: 2, textTransform: "none", color: "text.primary" }}
        >
          Sign In
        </Button>
        <Button
          component={RouterLink}
          to="/login?mode=register"
          variant="contained"
          fullWidth={stacked}
          onClick={close}
          sx={{ fontWeight: 700, borderRadius: 2, textTransform: "none" }}
        >
          Start Free
        </Button>
      </>
    )
  );

  const actions = (stacked = false) =>
    (renderActions ? renderActions({ stacked, close }) : authButtons(stacked));

  const wide = { xs: "none", [navBreakpoint]: "flex" };
  const narrow = { xs: "flex", [navBreakpoint]: "none" };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky", top: 0, zIndex: 1100,
        // Quiet at the top of the page, glass once the page scrolls under it.
        backdropFilter: elevated ? "blur(14px)" : "none",
        WebkitBackdropFilter: elevated ? "blur(14px)" : "none",
        bgcolor: (t) => {
          if (!elevated) return "transparent";
          return t.palette.mode === "dark" ? "rgba(6,10,15,0.78)" : "rgba(255,255,255,0.82)";
        },
        borderBottom: "1px solid",
        borderColor: elevated ? "divider" : "transparent",
        transition: "border-color 0.25s, box-shadow 0.25s, background-color 0.25s",
        boxShadow: elevated ? (t) => t.palette.mode === "dark" ? "0 1px 0 rgba(255,255,255,0.04)" : "0 1px 3px rgba(15,23,42,0.06)" : "none",
      }}
    >
      <Container maxWidth="lg" sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, height: { xs: 60, md: 68 }, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <PesaFiiBrand linkTo="/" ariaLabel="PesaFii home" />
            {brandSuffix && (
              <Box
                component="span"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  alignItems: "center",
                  pl: 1.25,
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  fontWeight: 700,
                  fontSize: 15,
                  whiteSpace: "nowrap",
                }}
              >
                {brandSuffix}
              </Box>
            )}
          </Stack>

          {/* Desktop nav */}
          <Stack component="nav" aria-label="Main" direction="row" spacing={0.5} sx={{ display: wide, alignItems: "center" }}>
            {navLinks.map((l) => <NavLink key={linkKey(l)} link={l} inView={l.href === `/#${inView}`} />)}
          </Stack>

          {/* Desktop actions */}
          <Stack direction="row" spacing={1} sx={{ display: wide, alignItems: "center" }}>
            <ThemeToggle />
            {actions()}
          </Stack>

          {/* Mobile actions — theme toggle stays visible next to the menu button */}
          <Stack direction="row" spacing={1} sx={{ display: narrow, alignItems: "center" }}>
            {mobileAction && (
              <Box sx={{ display: { xs: "none", sm: "inline-flex" } }}>{mobileAction}</Box>
            )}
            <ThemeToggle />
            <IconButton
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="mobile-nav-drawer"
              sx={{ width: 40, height: 40, border: "1px solid", borderColor: "divider", borderRadius: 2, color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
          </Stack>
        </Box>
      </Container>

      {/* Mobile drawer */}
      <Drawer
        id="mobile-nav-drawer"
        anchor="right"
        open={open}
        onClose={close}
        PaperProps={{ sx: { width: { xs: "82vw", sm: 340 }, maxWidth: 360, bgcolor: "background.default", p: 2 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <PesaFiiBrand linkTo="/" ariaLabel="PesaFii home" onNavigate={close} />
          <IconButton onClick={close} aria-label="Close menu" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box component="nav" aria-label="Main">
        <List>
          {navLinks.map((l) => (
            <ListItem key={linkKey(l)} disablePadding>
              <ListItemButton {...linkProps(l)} onClick={close} sx={{ borderRadius: 2, minHeight: 48 }}>
                <ListItemText primaryTypographyProps={{ fontWeight: 600, color: "text.primary" }} primary={l.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {actions(true)}
        </Stack>
      </Drawer>
    </Box>
  );
}
